import { create } from 'zustand';
import { db } from '@/lib/db/db';
import { supabase } from '@/lib/supabase';
import type {
    OnboardingProfile,
    OnboardingStep,
    Role,
    ExperienceBand,
    Level,
    WrestlingStyle,
    Goal,
    DaysPerWeek,
    SessionMinutes,
    FocusRatings,
} from '@/types/onboarding';

/* ─── Defaults ──────────────────────────────────────────────── */

const DEFAULT_PROFILE: Omit<OnboardingProfile, 'id' | 'athleteId' | 'completedAt'> = {
    role: 'athlete',
    yearsBand: '0-1',
    level: 'youth',
    goals: [],
    daysPerWeek: 3,
    sessionMinutes: 20,
};

/* ─── State ─────────────────────────────────────────────────── */

interface OnboardingState {
    step: OnboardingStep;
    profile: Partial<OnboardingProfile>;
    completed: boolean;
    loaded: boolean;

    // Actions
    hydrate: (athleteId: string) => Promise<void>;
    setRole: (role: Role) => void;
    setYearsBand: (band: ExperienceBand) => void;
    setLevel: (level: Level) => void;
    setStyle: (style: WrestlingStyle) => void;
    setGoals: (goals: Goal[]) => void;
    setDaysPerWeek: (days: DaysPerWeek) => void;
    setSessionMinutes: (mins: SessionMinutes) => void;
    setFocus: (focus: FocusRatings) => void;

    // Deep Profile Setters
    setMatIdentity: (id: import('@/types/onboarding').MatIdentity) => void;
    setTopAttack: (attack: import('@/types/onboarding').Attack) => void;
    setDominatePosition: (pos: import('@/types/onboarding').DominatePosition) => void;
    setPhysicalStats: (height: string, weight: number, weightClass: string, activity: import('@/types/onboarding').ActivityLevel) => void;
    setTrainingSetup: (freq: import('@/types/onboarding').TrainingFrequency, intensity: import('@/types/onboarding').TrainingIntensity) => void;
    setSchedule: (schedule: import('@/types/onboarding').WeeklySchedule) => void;

    nextStep: () => void;
    prevStep: () => void;
    skip: () => Promise<void>;
    complete: () => Promise<void>;
    reset: () => Promise<void>;
}

/* ─── Store ──────────────────────────────────────────────────── */

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
    step: 0 as OnboardingStep,
    profile: { ...DEFAULT_PROFILE },
    completed: false,
    loaded: false,

    hydrate: async (athleteId: string) => {
        // Check for in-progress or completed profile
        const existing = await db.onboardingProfiles
            .where('athleteId')
            .equals(athleteId)
            .first();

        if (existing) {
            if (existing.completedAt) {
                set({ completed: true, loaded: true, profile: existing });
                return;
            }

            // Calculate which step to resume at based on filled fields
            let resumeStep: OnboardingStep = 0;
            if (existing.role) resumeStep = 1;
            if (existing.yearsBand && existing.level) resumeStep = 2;
            if (existing.goals && existing.goals.length > 0) resumeStep = 3;
            if (existing.daysPerWeek && existing.sessionMinutes) resumeStep = 4;

            set({ profile: existing, step: resumeStep, loaded: true, completed: false });
        } else {
            set({ profile: { ...DEFAULT_PROFILE, athleteId }, loaded: true, completed: false, step: 0 });
        }
    },

    setRole: (role) => {
        set((s) => ({ profile: { ...s.profile, role } }));
        _persist(get);
    },

    setYearsBand: (yearsBand) => {
        set((s) => ({ profile: { ...s.profile, yearsBand } }));
        _persist(get);
    },

    setLevel: (level) => {
        set((s) => ({ profile: { ...s.profile, level } }));
        _persist(get);
    },

    setStyle: (style) => {
        set((s) => ({ profile: { ...s.profile, style } }));
        _persist(get);
    },

    setGoals: (goals) => {
        set((s) => ({ profile: { ...s.profile, goals: goals.slice(0, 3) } }));
        _persist(get);
    },

    setDaysPerWeek: (daysPerWeek) => {
        set((s) => ({ profile: { ...s.profile, daysPerWeek } }));
        _persist(get);
    },

    setSessionMinutes: (sessionMinutes) => {
        set((s) => ({ profile: { ...s.profile, sessionMinutes } }));
        _persist(get);
    },

    setFocus: (focus) => {
        set((s) => ({ profile: { ...s.profile, focus } }));
        _persist(get);
    },

    setMatIdentity: (matIdentity) => {
        set((s) => ({ profile: { ...s.profile, matIdentity } }));
        _persist(get);
    },

    setTopAttack: (topAttack) => {
        set((s) => ({ profile: { ...s.profile, topAttack } }));
        _persist(get);
    },

    setDominatePosition: (dominatePosition) => {
        set((s) => ({ profile: { ...s.profile, dominatePosition } }));
        _persist(get);
    },

    setPhysicalStats: (height, weight, weightClass, activityLevel) => {
        set((s) => ({ profile: { ...s.profile, height, weight, weightClass, activityLevel } }));
        _persist(get);
    },

    setTrainingSetup: (trainingFrequency, trainingIntensity) => {
        set((s) => ({ profile: { ...s.profile, trainingFrequency, trainingIntensity } }));
        _persist(get);
    },

    setSchedule: (schedule) => {
        set((s) => ({ profile: { ...s.profile, schedule } }));
        _persist(get);
    },

    nextStep: () => {
        set((s) => ({ step: s.step + 1 }));
    },

    prevStep: () => {
        set((s) => ({ step: Math.max(s.step - 1, 0) }));
    },

    skip: async () => {
        const profile = { ...DEFAULT_PROFILE, athleteId: get().profile.athleteId ?? 'ath-1' };
        const now = new Date().toISOString();
        const final = { ...profile, completedAt: now } as OnboardingProfile;
        await db.onboardingProfiles.put(final);
        set({ completed: true, profile: final });

        // Persist to Supabase so it survives cache clears
        supabase.auth.updateUser({ data: { onboarding_completed: true } }).catch(console.error);
    },

    complete: async () => {
        const p = get().profile;
        const now = new Date().toISOString();
        const final: OnboardingProfile = {
            ...DEFAULT_PROFILE,
            ...p,
            athleteId: p.athleteId ?? 'ath-1',
            completedAt: now,
        } as OnboardingProfile;

        await db.onboardingProfiles.put(final);
        set({ completed: true, profile: final });

        // Persist to Supabase so it survives cache clears
        supabase.auth.updateUser({ data: { onboarding_completed: true } }).catch(console.error);
    },

    reset: async () => {
        const athleteId = get().profile.athleteId ?? 'ath-1';
        await db.onboardingProfiles.where('athleteId').equals(athleteId).delete();
        set({ step: 0, completed: false, profile: { ...DEFAULT_PROFILE, athleteId } });
    },
}));

/* ─── Dexie Persistence Helper ──────────────────────────────── */

async function _persist(get: () => OnboardingState) {
    const p = get().profile;
    if (!p.athleteId) return;

    try {
        const id = await db.onboardingProfiles.put(p as OnboardingProfile);
        // Store the generated ID back if it was new
        if (!p.id) get().profile.id = id;
    } catch {
        // Silently fail — offline resilience
    }
}
